import { faPhone, faEnvelope } from '@fortawesome/pro-regular-svg-icons';
import { faDownload, faExternalLink } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { ComponentPropsWithoutRef, ComponentType, FC } from 'react';
import { useMemo } from 'react';
import type { LinkProps as RouterLinkProps } from 'react-router';
import { Link as RouterLink, useHref, useLocation } from 'react-router';
import type { ActionMode } from 'src/components/action-mode';
import { useActionMode } from 'src/components/action-mode';
import { useActionGroupMode } from 'src/components/buttons-and-actions/action-group';
import {
  getBaseActionClasses,
  getDropdownItemClasses,
  getButtonInverseClasses,
  getButtonPrimaryClasses,
  getButtonSecondaryClasses,
  type Size,
  getTabClasses,
} from 'src/components/buttons-and-actions/button';
import type { IconProps } from 'src/components/media-and-icons/icon';
import { isProductFruitsOpen } from 'src/components/product-fruits/utils';
import { useIsNewDesignActive } from 'src/pages/new-design/is-new-design';
import { cn } from 'src/utils/cn';
import { hasOpenToast } from 'src/utils/has-open-toast';
import { isNewReleaseAvailable } from 'src/utils/is-new-release-available';
import { locationReplace, setLocationHref } from 'src/utils/location-usage';
import type { StrictOmit } from 'src/utils/utility-types';

export type BaseLinkProps =
  | {
      mode?:
        | 'default'
        | 'inverse' // maybe we can delete this when we delete the legacy styles (only used in the footer)
        | 'button-inverse'; // maybe we can delete this when we delete the legacy styles (only used in menu)
      /** This mode doesn't use `icon`, but you can pass it, in case mode is switched on dynamically. */
      icon?: ComponentType<IconProps>;
      size?: undefined;
      /** This mode doesn't use `isActive` or `isSelected`, but you can pass it, in case mode is switched on dynamically. */
      isActive?: boolean;
      isSelected?: boolean;
      hideIcon?: undefined;
      /** This mode doesn't use `disabled`, but you can pass it, in case mode is switched on dynamically. */
      disabled?: boolean;
    }
  | {
      mode?: 'default-underlined';
      icon?: undefined;
      size?: undefined;
      isActive?: undefined;
      isSelected?: undefined;
      /**
       * This is only relevant for external links. *Very* carefully use this.
       * E.g. if we point to an external site belonging to Prisma. This icon is meant
       * to be an indicator that the user is redirected to a 3rd party site.
       */
      hideIcon?: boolean;
      disabled?: undefined;
    }
  | {
      mode:
        | 'primary' // maybe we can delete this when we delete the legacy styles (only used in the header)
        | 'secondary' // maybe we can delete this when we delete the legacy styles (only used in the header)
        | 'tab';
      icon?: undefined;
      size?: undefined;
      isActive?: boolean;
      isSelected?: undefined;
      hideIcon?: undefined;
      disabled?: undefined;
    }
  | {
      mode: 'sidebar-item';
      icon?: undefined;
      size?: undefined;
      isActive?: boolean;
      isSelected?: undefined;
      hideIcon?: undefined;
      disabled?: boolean; // TODO: check if still needed (this was never really used for disabling the link)
    }
  | {
      mode: 'button-primary';
      icon?: undefined;
      size?: Size;
      /** This mode doesn't use `isActive`, but you can pass it, in case mode is switched dynamically. */
      isActive?: boolean;
      isSelected?: undefined;
      hideIcon?: undefined;
      disabled?: undefined;
    }
  | {
      mode: 'button-secondary';
      icon?: undefined;
      size?: Size;
      isActive?: undefined;
      isSelected?: undefined;
      hideIcon?: undefined;
      disabled?: undefined;
    }
  | {
      mode: 'segmented-control';
      icon?: undefined;
      size?: undefined;
      isActive?: undefined;
      isSelected?: boolean;
      hideIcon?: undefined;
      disabled?: undefined;
    }
  | {
      mode: 'dropdown-item';
      icon?: undefined;
      size?: undefined;
      isActive?: boolean;
      isSelected?: undefined;
      hideIcon?: undefined;
      disabled?: boolean;
    }
  | {
      mode: 'navigation-item'; // used in the app shell navigation
      icon?: ComponentType<IconProps>;
      size?: undefined;
      isActive?: boolean;
      isSelected?: undefined;
      hideIcon?: undefined;
      disabled?: undefined;
    }
  | {
      mode: 'navigation-child-item'; // used in the app shell navigation
      icon?: undefined;
      size?: undefined;
      isActive?: boolean;
      isSelected?: undefined;
      hideIcon?: undefined;
      disabled?: undefined;
    };

const routeAwareActiveness: BaseLinkProps['mode'][] = [
  'primary',
  'secondary',
  'tab',
  'dropdown-item',
  'sidebar-item',
  'navigation-item',
  'navigation-child-item',
];

export type LinkProps<S> = BaseLinkProps &
  StrictOmit<RouterLinkProps, 'state'> & {
    state?: S;
  };

export const Link: FC<LinkProps<any>> = (baseProps) => {
  const actionMode = useActionMode();
  const { pathname } = useLocation();
  const actionGroupMode = useActionGroupMode();
  const isNewDesignActive = useIsNewDesignActive();

  // descruct all custom props
  const {
    mode,
    size = 'md',
    isActive: explicitIsActive,
    isSelected: explicitIsSelected,
    icon: IconComponent,
    hideIcon,
    disabled,
    ...props
  } = {
    ...mapActionModeToLink(actionMode),
    ...baseProps,
  } as LinkProps<any>;

  // can be overwritten by passing `isActive` explicitly
  const isActive = useMemo(() => {
    if (explicitIsActive !== undefined) return explicitIsActive;
    if (!routeAwareActiveness.includes(mode)) return false;
    const to =
      typeof props.to === 'string'
        ? props.to
        : typeof props.to === 'function'
          ? '' // note: function version is not supported
          : props.to.pathname || '';
    return pathname.startsWith(to.replace(`${PRISMA_CONFIG.angularUrl}/#`, ''));
  }, [pathname, props.to, explicitIsActive, mode]);

  const segmentedControlSelected = explicitIsSelected ?? isActive;

  switch (mode) {
    case 'primary':
      return (
        <CustomRouterLink
          disabled={disabled}
          {...props}
          className={cn(
            getBaseActionClasses({ isActive }),
            `
              border-b-2 border-b-transparent text-body-lg text-white
              no-underline
              hover:border-b-white hover:text-white
              focus:border-b-white focus:text-white
            `,
            isActive && 'border-b-white text-white',
            props.className
          )}
        />
      );
    case 'secondary':
      return (
        <CustomRouterLink
          disabled={disabled}
          {...props}
          className={cn(
            getBaseActionClasses({ isActive }),
            `
              border-b-2 border-b-transparent text-body-lg text-legacy-brand
              no-underline
              hover:border-b-legacy-brand hover:text-legacy-brand
              focus:border-b-legacy-brand focus:text-legacy-brand
            `,
            isActive && 'border-b-legacy-brand text-legacy-brand',
            props.className
          )}
        />
      );
    case 'tab':
      return (
        <CustomRouterLink
          disabled={disabled}
          {...props}
          className={getTabClasses({
            isActive,
            className: props.className,
          })}
          // We use this to track the active tab item in the `Tabs` component for scroll when overflowing
          data-active={isActive}
        />
      );
    case 'inverse':
      return (
        <CustomRouterLink
          disabled={disabled}
          {...props}
          className={cn(
            getBaseActionClasses({ isActive }),
            `
              text-label-base text-white no-underline
              hover:text-legacy-brand-secondary
              focus:text-legacy-brand-secondary
            `,
            props.className
          )}
        />
      );
    case 'dropdown-item':
      return (
        <CustomRouterLink
          disabled={disabled}
          {...props}
          className={getDropdownItemClasses({
            isActive,
            className: cn(props.className),
          })}
        >
          {isNewDesignActive ? props.children : <span>{props.children}</span>}
        </CustomRouterLink>
      );
    case 'button-primary':
      return (
        <CustomRouterLink
          disabled={disabled}
          {...props}
          className={getButtonPrimaryClasses({
            actionGroupMode,
            size,
            className: cn('inline-block', props.className),
          })}
        />
      );
    case 'button-secondary':
      return (
        <CustomRouterLink
          disabled={disabled}
          {...props}
          className={getButtonSecondaryClasses({
            isSelected: undefined,
            actionGroupMode,
            size,
            className: cn('inline-block', props.className),
          })}
        />
      );
    case 'button-inverse':
      return (
        <CustomRouterLink
          disabled={disabled}
          {...props}
          className={getButtonInverseClasses({
            actionGroupMode,
            className: cn('inline-block', props.className),
          })}
        />
      );
    case 'sidebar-item':
      return (
        <CustomRouterLink
          disabled={disabled}
          {...props}
          className={getSidebarItemClasses({
            isActive,
            className: props.className,
          })}
        >
          {isNewDesignActive ? props.children : <span>{props.children}</span>}
        </CustomRouterLink>
      );
    case 'segmented-control':
      return (
        <CustomRouterLink
          disabled={segmentedControlSelected}
          {...props}
          className={getButtonSecondaryClasses({
            isSelected: segmentedControlSelected,
            actionGroupMode,
            size: 'sm',
            className: props.className,
          })}
        />
      );
    case 'navigation-item': {
      return (
        <CustomRouterLink
          {...props}
          className={cn(
            getNavigationItemStyles({
              className: props.className,
              isActive,
            })
          )}
          data-slot="sidebar-navigation-item"
          data-active={isActive ? true : undefined}
        >
          {IconComponent && <IconComponent size="sm" className="shrink-0" />}
          <div className="grow truncate">{props.children}</div>
        </CustomRouterLink>
      );
    }
    case 'navigation-child-item':
      return (
        <div className="relative px-xl py-2xs pl-2xl">
          <CustomRouterLink
            {...props}
            className={cn(
              `
                block rounded-xs px-md py-xs text-foreground-sidebar-default
                transition-[color,border-color,background-color] duration-150
                ease-in-out
              `,
              `
                truncate
                group-data-[sidebar-expanded=true]:group-data-[sidebar-transitioning=false]:truncate-none
              `,
              `
                before:pointer-events-none before:absolute before:top-0
                before:left-lg before:block before:h-full before:w-px
                before:bg-border-sidebar-minimal before:transition-colors
                before:duration-150 before:ease-in-out
                before:motion-reduce:transition-none
              `,
              `
                hover:text-foreground-sidebar-default!
                hover:before:bg-border-sidebar-default
              `,
              !isActive && 'hover:bg-background-sidebar-hover',
              isActive &&
                `
                  bg-background-sidebar-selected font-body-semibold
                  text-foreground-sidebar-selected
                  before:bg-border-sidebar-default
                  hover:text-foreground-sidebar-selected!
                `,
              props.className
            )}
            data-slot="sidebar-navigation-child-item"
            data-active={isActive ? true : undefined}
          />
        </div>
      );
    case 'default-underlined':
      return (
        <CustomRouterLink
          disabled={disabled}
          {...props}
          className={getDefaultUnderlinedLinkStyles({
            className: props.className,
          })}
        />
      );
    case 'default':
    case undefined:
      return (
        <CustomRouterLink
          disabled={disabled}
          {...props}
          className={getDefaultLinkStyles({
            className: props.className,
          })}
        />
      );
  }
};

const disabledLinkProps: ComponentPropsWithoutRef<'a'> & {
  'data-force-disabled': true;
} = {
  href: undefined,
  role: 'link',
  'aria-disabled': true,
  'data-force-disabled': true,
};

export type ExternalLinkProps = BaseLinkProps &
  ComponentPropsWithoutRef<'a'> & {
    /**
     * This is needed if we do NOT want to convert an internal link from
     * <ExternalLink/> to <Link/>, because we WANT the refresh.
     */
    forceRefresh?: boolean;
  };

export const ExternalLink: FC<ExternalLinkProps> = ({
  disabled,
  ...baseProps
}) => {
  const actionMode = useActionMode();
  const { pathname, hash } = useLocation();
  const actionGroupMode = useActionGroupMode();
  const isNewDesignActive = useIsNewDesignActive();

  // see https://web.dev/external-anchors-use-rel-noopener
  const unsafeCrossOriginDestination =
    baseProps.target === '_blank' && !baseProps.rel;

  // descruct all custom props
  const {
    mode,
    size = 'md',
    isActive: explicitIsActive,
    isSelected: explicitIsSelected,
    icon: IconComponent,
    hideIcon,
    forceRefresh,
    ...props
  } = {
    ...mapActionModeToLink(actionMode),
    ...baseProps,
    ...(unsafeCrossOriginDestination && { rel: 'noopener noreferrer' }),
    ...(disabled && disabledLinkProps),
  } as ExternalLinkProps;

  // can be overwritten by passing `isActive` explicitly
  const isActive = useMemo(() => {
    if (explicitIsActive !== undefined) return explicitIsActive;
    if (!routeAwareActiveness.includes(mode)) return false;

    if (props.href) {
      const cleanedHref = props.href.replace(
        `${PRISMA_CONFIG.angularUrl}/#`,
        ''
      );
      return pathname === '/platform' || pathname === '/platform/'
        ? hash.startsWith(`#${cleanedHref}`)
        : pathname.startsWith(cleanedHref);
    } else {
      return false;
    }
  }, [pathname, hash, props.href, explicitIsActive, mode]);

  const segmentedControlSelected = explicitIsSelected ?? isActive;

  // links to the old frontend used to point to a standalone Angular application
  // in the past (and therefor we used external links), but since we started to
  // embed Angular pages into React we manually convert them to internal links
  if (props.href?.startsWith(PRISMA_CONFIG.angularUrl) && !forceRefresh) {
    const to = props.href.replace(PRISMA_CONFIG.angularUrl, '/platform');
    const { href, ...baseLinkProps } = props;
    const linkProps = {
      ...baseLinkProps,
      isActive,
      to,
      mode,
      size,
      disabled,
    } as LinkProps<any>;
    return <Link {...linkProps} />;
  }

  switch (mode) {
    case 'primary':
      return (
        <a
          {...props}
          className={cn(
            getBaseActionClasses({ isActive }),
            `
              border-b-2 border-b-transparent text-body-lg text-white
              no-underline
              hover:border-b-white hover:text-white
              focus:border-b-white focus:text-white
            `,
            isActive && 'border-b-white text-white',
            props.className
          )}
        />
      );
    case 'secondary':
      return (
        <a
          {...props}
          className={cn(
            getBaseActionClasses({ isActive }),
            `
              border-b-2 border-b-transparent text-body-lg text-legacy-brand
              no-underline
              hover:border-b-legacy-brand hover:text-legacy-brand
              focus:border-b-legacy-brand focus:text-legacy-brand
            `,
            isActive && 'border-b-legacy-brand text-legacy-brand',
            props.className
          )}
        />
      );
    case 'tab':
      return (
        <a
          {...props}
          className={getTabClasses({
            isActive,
            className: props.className,
          })}
        />
      );
    case 'inverse':
      return (
        <a
          {...props}
          className={cn(
            getBaseActionClasses({ isActive }),
            `
              text-label-base text-white no-underline
              hover:text-legacy-brand-secondary
              focus:text-legacy-brand-secondary
            `,
            props.className
          )}
        />
      );
    case 'dropdown-item':
      return (
        <a
          {...props}
          className={getDropdownItemClasses({
            isActive,
            className: cn(props.className),
          })}
        >
          {isNewDesignActive ? props.children : <span>{props.children}</span>}
        </a>
      );
    case 'button-primary':
      return (
        <a
          {...props}
          className={getButtonPrimaryClasses({
            actionGroupMode,
            size,
            className: cn('inline-block', props.className),
          })}
        />
      );
    case 'button-secondary':
      return (
        <a
          {...props}
          className={getButtonSecondaryClasses({
            isSelected: undefined,
            actionGroupMode,
            size,
            className: cn('inline-block', props.className),
          })}
        />
      );
    case 'button-inverse':
      return (
        <a
          {...props}
          className={getButtonInverseClasses({
            actionGroupMode,
            className: cn('inline-block', props.className),
          })}
        />
      );
    case 'sidebar-item':
      return (
        <a
          {...props}
          className={getSidebarItemClasses({
            isActive,
            className: props.className,
          })}
        >
          {isNewDesignActive ? props.children : <span>{props.children}</span>}
        </a>
      );
    case 'segmented-control':
      return (
        <a
          {...props}
          {...(segmentedControlSelected && disabledLinkProps)}
          className={getButtonSecondaryClasses({
            isSelected: segmentedControlSelected,
            actionGroupMode,
            size: 'sm',
            className: props.className,
          })}
        />
      );
    case 'navigation-item': {
      return (
        <a
          {...props}
          className={cn(
            getNavigationItemStyles({
              className: props.className,
            })
          )}
        >
          {IconComponent && <IconComponent size="sm" className="shrink-0" />}

          <div className="grow truncate">{props.children}</div>
        </a>
      );
    }
    case 'navigation-child-item':
      throw new Error(
        'ExternalLinks currently do not support action mode="navigation-child-item".'
      );
    case 'default-underlined':
      return (
        <span>
          <a
            {...props}
            className={getDefaultUnderlinedLinkStyles({
              className: props.className,
            })}
          />
          {!hideIcon && (
            <>
              {' '}
              <FontAwesomeIcon
                className="text-foreground-primary-inverted"
                icon={
                  props.download || isFileUri(props.href)
                    ? faDownload
                    : props.href?.startsWith('tel:')
                      ? faPhone
                      : props.href?.startsWith('mailto:')
                        ? faEnvelope
                        : faExternalLink
                }
              />
            </>
          )}
        </span>
      );
    case 'default':
    case undefined:
      return (
        <a
          {...props}
          className={getDefaultLinkStyles({
            className: props.className,
          })}
        />
      );
  }
};

function getSidebarItemClasses({
  isActive,
  className,
}: {
  isActive?: boolean;
  className?: string;
}) {
  return cn(
    getBaseActionClasses({ isActive }),
    `
      text-body-base text-foreground-secondary-default
      hover:text-foreground-secondary-hover
    `,
    isActive &&
      `
        font-body-semibold text-foreground-secondary-active
        hover:text-foreground-secondary-active
      `,
    className
  );
}

export function getDefaultLinkStyles({ className }: { className?: string }) {
  return cn(
    getBaseActionClasses({ isActive: undefined }),
    `
      text-label-base text-foreground-primary-inverted no-underline
      not-active:hover:text-foreground-secondary-hover
      focus-visible:spaced-focus-style
      active:text-foreground-secondary-active
      disabled:text-foreground-secondary-disabled
      disabled:hover:text-foreground-secondary-disabled
    `,
    className
  );
}

export function getDefaultUnderlinedLinkStyles({
  className,
}: {
  className?: string;
}) {
  return getDefaultLinkStyles({
    className: cn(className),
  });
}

export function getNavigationItemStyles({
  className,
  isActive,
}: {
  className?: string;
  isActive?: boolean;
}) {
  return cn(
    getBaseActionClasses({ isActive }),
    `
      flex min-h-3xl w-full cursor-pointer items-center gap-md rounded-xs px-md
      py-sm text-start text-foreground-sidebar-default duration-150 ease-in-out
      group-data-[sidebar-expanded=false]:min-w-3xl
      motion-reduce:transition-none
    `,
    !isActive &&
      `
        hover:bg-background-sidebar-hover hover:text-foreground-sidebar-default
        aria-[haspopup=menu]:aria-expanded:bg-background-sidebar-hover
        aria-[haspopup=menu]:aria-expanded:text-foreground-sidebar-default
      `,
    isActive &&
      `
        bg-background-sidebar-selected font-body-semibold
        text-foreground-sidebar-selected
        hover:text-foreground-sidebar-selected
      `,
    className
  );
}

// allow "disabled" state
const CustomRouterLink: FC<
  { disabled?: boolean } & RouterLinkProps &
    StrictOmit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>
> = ({
  // custom props
  disabled,
  // router link props
  reloadDocument,
  replace,
  state,
  preventScrollReset,
  relative,
  to,
  viewTransition,
  onClick,
  // native props
  ...rest
}) => {
  const href = useHref(to, relative ? { relative } : undefined);

  const shouldHardReloadOnNavigate = () => {
    if (!isNewReleaseAvailable()) return false;
    if (hasOpenToast()) return false;
    if (state != null) return false;
    if (isProductFruitsOpen()) return false;
    return true;
  };

  const handleClick: React.MouseEventHandler<HTMLAnchorElement> = (event) => {
    onClick?.(event);
    if (event.defaultPrevented) return;
    if (!shouldHardReloadOnNavigate()) {
      return;
    }
    // if new release was detected, do full browser refresh so new release is loaded
    event.preventDefault();
    if (replace) {
      locationReplace(href);
    } else {
      setLocationHref(href);
    }
  };

  if (disabled) {
    return <a {...rest} {...disabledLinkProps} />;
  }

  return (
    <RouterLink
      {...rest}
      onClick={handleClick}
      reloadDocument={reloadDocument}
      replace={replace}
      state={state}
      preventScrollReset={preventScrollReset}
      relative={relative}
      to={to}
      viewTransition={viewTransition}
    />
  );
};

function isFileUri(uri?: string) {
  if (!uri) return false;
  try {
    const url = new URL(uri);
    return /\/[^/]+\.[^/]+$/.test(url.pathname);
  } catch (err) {
    // sadly in the monolith we sometimes have invalid urls
    return false;
  }
}

function mapActionModeToLink(
  actionMode: ActionMode | null
): BaseLinkProps | null {
  if (!actionMode) return null;

  switch (actionMode.mode) {
    case 'button-primary':
      return { ...actionMode, mode: 'button-primary' };
    case 'button-secondary':
      return { ...actionMode, mode: 'button-secondary' };
    case 'link-primary':
      return { ...actionMode, mode: 'primary' };
    case 'dropdown':
      return { ...actionMode, mode: 'dropdown-item' };
    case 'segmented-control':
      return { ...actionMode, mode: 'segmented-control' };
    case 'sidebar-item':
      return { ...actionMode, mode: 'sidebar-item' };
    case 'tab':
      return { ...actionMode, mode: 'tab' };
    case 'navigation-item':
      return { ...actionMode, mode: 'navigation-item' };
    case 'navigation-child-item':
      return { ...actionMode, mode: 'navigation-child-item' };
  }
}
